'use client';

import React from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface DataPoint {
  date: string;
  label: string;
  count: number;
}

interface Props {
  data: DataPoint[];
}

const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number }>; label?: string }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-xl border px-4 py-3 shadow-elevated"
      style={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))' }}
    >
      <p className="text-xs font-medium mb-1" style={{ color: 'hsl(var(--muted-foreground))' }}>{label}</p>
      <p className="font-bold text-sm tabular-nums" style={{ color: 'hsl(var(--accent))' }}>
        {payload[0].value} {payload[0].value === 1 ? 'intervencija' : 'intervencija'}
      </p>
    </div>
  );
};

export default function ReportDailyChart({ data }: Props) {
  if (data.length === 0) return null;

  // Only show every Nth label if too many days
  const tickInterval = data.length > 20 ? Math.ceil(data.length / 10) - 1 : 0;

  return (
    <div className="card p-5">
      <h3 className="font-semibold text-base mb-1" style={{ color: 'hsl(var(--foreground))' }}>
        Dnevni broj intervencija
      </h3>
      <p className="text-xs mb-5" style={{ color: 'hsl(var(--muted-foreground))' }}>
        Volumen intervencija po danu u izabranom periodu
      </p>
      <ResponsiveContainer width="100%" height={240}>
        <AreaChart data={data} margin={{ top: 16, right: 16, left: -8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 20% 88%)" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: 'hsl(215 15% 50%)', fontFamily: 'DM Sans' }}
            tickLine={false}
            axisLine={false}
            interval={tickInterval}
          />
          <YAxis
            tick={{ fontSize: 11, fill: 'hsl(215 15% 50%)', fontFamily: 'DM Sans' }}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="count"
            stroke="hsl(217 91% 60%)"
            strokeWidth={2}
            fill="url(#gradDaily)"
            dot={(props) => {
              const { cx, cy, payload } = props;
              if (payload.count === 0) return <g key={`dot-empty-${payload.date}`} />;
              return (
                <circle
                  key={`dot-${payload.date}`}
                  cx={cx}
                  cy={cy}
                  r={3}
                  fill="hsl(217 91% 60%)"
                  stroke="white"
                  strokeWidth={2}
                />
              );
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}